import { useState, useEffect } from 'react';
import { useRequestOtp, useVerifyOtp, useLogin, useGoogleLogin, useRegisterComplete } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone, Envelope, Key, GoogleLogo, Eye, EyeSlash, User } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';
import useAppStore from '@/stores/appStore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { authApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/shared/LoadingStates';

export const Login = () => {
  const location = useLocation();
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const isOps = location.pathname.startsWith('/ops');

  // View state: 'default' | 'alternative'
  // - Customer default: WhatsApp OTP
  // - Ops default: Google SSO + Email/Password form
  const [view, setView] = useState('default');
  
  // OTP flow states
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [phoneBody, setPhoneBody] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'register_details'
  
  // Register details states
  const [registrationToken, setRegistrationToken] = useState('');
  const [regFullName, setRegFullName] = useState('');

  const registerCompleteMutation = useRegisterComplete();
  
  // Credentials flow states
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Forgot password flow states
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState('phone');
  const [resetPhoneBody, setResetPhoneBody] = useState('');
  const [resetCountryCode, setResetCountryCode] = useState('+91');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [requestingReset, setRequestingReset] = useState(false);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const loginCredentials = useLogin();
  const googleLogin = useGoogleLogin();

  // Cooldown countdown timer effect
  useEffect(() => {
    let interval = null;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleRequestOtp = async (e) => {
    if (e) e.preventDefault();
    if (!phoneBody) {
      toast.error('Please enter a phone number');
      return;
    }
    if (phoneBody.length !== 10) {
      toast.error('Please enter a valid 10-digit WhatsApp number');
      return;
    }
    const fullPhone = `${countryCode}${phoneBody}`;
    try {
      setPhone(fullPhone);
      await requestOtp.mutateAsync({ phone: fullPhone, portal: isOps ? 'ops' : 'customer' });
      toast.success('OTP sent via WhatsApp!');
      setResendCooldown(60);
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    const fullPhone = `${countryCode}${phoneBody}`;
    try {
      await requestOtp.mutateAsync({ phone: fullPhone, portal: isOps ? 'ops' : 'customer' });
      toast.success('New OTP sent via WhatsApp!');
      setResendCooldown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    try {
      const response = await verifyOtp.mutateAsync({ phone, otp, portal: isOps ? 'ops' : 'customer' });
      if (response?.data?.registration_required) {
        setRegistrationToken(response.data.registration_token);
        setStep('register_details');
        toast.info('Verification successful! Please complete your name to sign up.');
      } else {
        const user = response?.data?.user;
        toast.success(`Welcome back, ${user?.full_name || 'User'}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
      setOtp('');
    }
  };

  const handleRegisterDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!regFullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    try {
      const response = await registerCompleteMutation.mutateAsync({
        registration_token: registrationToken,
        full_name: regFullName.trim(),
      });
      const user = response?.data?.user;
      toast.success(`Registration complete! Welcome, ${user?.full_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete registration');
    }
  };

  const handleLoginCredentials = async (e) => {
    e.preventDefault();
    if (!emailOrPhone || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const response = await loginCredentials.mutateAsync({ 
        email_or_phone: emailOrPhone, 
        password, 
        portal: isOps ? 'ops' : 'customer' 
      });
      const user = response?.data?.user;
      toast.success(`Welcome back, ${user?.full_name || 'User'}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!resetPhoneBody.trim() || resetPhoneBody.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    const fullPhone = `${resetCountryCode}${resetPhoneBody.trim()}`;
    setRequestingReset(true);
    try {
      await authApi.requestPasswordReset(fullPhone);
      toast.success('Password reset OTP sent to WhatsApp!');
      setResetStep('otp');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to request password reset');
    } finally {
      setRequestingReset(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (!resetOtp || resetOtp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    const fullPhone = `${resetCountryCode}${resetPhoneBody.trim()}`;
    setConfirmingReset(true);
    try {
      await authApi.confirmPasswordReset(fullPhone, resetOtp, newPassword);
      toast.success('Password updated successfully! Please log in.');
      setShowResetModal(false);
      setEmailOrPhone(fullPhone);
      setResetStep('phone');
      setResetPhoneBody('');
      setResetOtp('');
      setNewPassword('');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setConfirmingReset(false);
    }
  };

  const handleGoogleCredentialResponse = async (response) => {
    const idToken = response.credential;
    if (!idToken) return;
    setIsAuthenticating(true);
    try {
      const resp = await googleLogin.mutateAsync({ 
        idToken, 
        portal: isOps ? 'ops' : 'customer' 
      });
      const user = resp?.data?.user;
      toast.success(`Welcome back, ${user?.full_name || 'User'}!`);
    } catch (error) {
      setIsAuthenticating(false);
      toast.error(error.response?.data?.detail || 'Google Login failed');
    }
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || clientId.includes("your-google-client-id")) return;


    let resizeObserver = null;

    const renderGoogleButton = () => {
      if (!window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredentialResponse,
      });
      const btnDiv = document.getElementById('googleLoginBtn');
      if (btnDiv) {
        const containerWidth = btnDiv.offsetWidth;
        const targetWidth = containerWidth ? Math.max(200, Math.min(containerWidth, 400)) : 320;
        btnDiv.innerHTML = '';
        window.google.accounts.id.renderButton(btnDiv, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          shape: 'rectangular',
          text: 'signin_with',
          logo_alignment: 'left',
          width: targetWidth,
        });
      }
    };

    const setupResizeObserver = () => {
      const btnDiv = document.getElementById('googleLoginBtn');
      if (btnDiv && window.ResizeObserver) {
        let lastWidth = btnDiv.offsetWidth;
        resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            const width = entry.contentRect.width;
            if (Math.abs(width - lastWidth) > 10) {
              lastWidth = width;
              renderGoogleButton();
            }
          }
        });
        resizeObserver.observe(btnDiv);
      }
    };

    let script = document.getElementById('google-jssdk');
    if (!script) {
      script = document.createElement('script');
      script.id = 'google-jssdk';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        renderGoogleButton();
        setupResizeObserver();
      };
      document.body.appendChild(script);
    } else {
      if (window.google) {
        renderGoogleButton();
        setupResizeObserver();
      } else {
        const oldOnload = script.onload;
        script.onload = (e) => {
          if (oldOnload) oldOnload(e);
          renderGoogleButton();
          setupResizeObserver();
        };
      }
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isOps]);

  const handleMockGoogleLogin = async () => {
    const mockEmail = prompt("Enter mock username for Google Sign In:", "john");
    if (!mockEmail) return;
    setIsAuthenticating(true);
    try {
      const response = await googleLogin.mutateAsync({ 
        idToken: `mock_${mockEmail}`, 
        portal: isOps ? 'ops' : 'customer' 
      });
      const user = response?.data?.user;
      toast.success(`Welcome back, ${user?.full_name || 'User'}!`);
    } catch (error) {
      setIsAuthenticating(false);
      toast.error(error.response?.data?.detail || 'Google Login failed');
    }
  };

  const whatsappEnabled = tenantConfig?.whatsapp_login_enabled !== false;
  const companyName = tenantConfig?.company_name || 'KWICC';
  const logoUrl = tenantConfig?.logo_url;
  const brandColor = tenantConfig?.brand_colour || '#002FA7';
  const interactiveColor = brandColor === '#002FA7' ? '#4f46e5' : brandColor;

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative overflow-hidden px-4"
      style={{
        background: `linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)`,
      }}
    >
      {(isAuthenticating || googleLogin.isPending) && <LoadingSpinner fullScreen />}
      {/* Subtle Decorative Orbs */}
      <div 
        className="absolute top-[-15%] left-[-10%] w-[50%] h-[50%] rounded-full blur-[160px]" 
        style={{ backgroundColor: brandColor, opacity: 0.06 }}
      />
      <div className="absolute bottom-[-15%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[160px]" style={{ backgroundColor: '#60a5fa', opacity: 0.05 }} />
      
      <div className="relative z-10 w-full max-w-md my-8">
        {/* Logo/Branding */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-2">
            <h1 className="text-4xl font-black text-slate-900 tracking-tight uppercase">KWICC</h1>
            {logoUrl && (
              <>
                <div className="h-6 w-[2px] bg-slate-300" />
                <img src={logoUrl} alt={companyName} className="h-10 object-contain" />
              </>
            )}
          </div>
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mt-1 mb-2">{companyName} Service Desk</p>
          <div className="mt-2">
            {isOps && (
              <span 
                className="px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded-sm"
                style={{ backgroundColor: interactiveColor }}
              >
                Operations Panel
              </span>
            )}
          </div>
        </div>

        <Card className="p-8 bg-white border border-slate-200 text-slate-900 shadow-[0_20px_50px_rgba(0,0,0,0.06)] rounded-xl relative overflow-hidden" data-testid="login-card">
          {/* Customer Portal View */}
          {!isOps && (
            view === 'default' ? (
              // Customer Portal: Default view is WhatsApp OTP
              <div className="space-y-6">
                {whatsappEnabled && step === 'phone' && (
                  <form onSubmit={handleRequestOtp} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="phoneBody" className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                        Phone Number (WhatsApp)
                      </Label>
                      <div className="flex gap-2">
                        <div className="w-[100px]">
                          <Select value={countryCode} onValueChange={setCountryCode}>
                            <SelectTrigger className="h-12 border-slate-300 bg-white text-slate-900 focus:border-primary">
                              <SelectValue placeholder="+91" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200 text-slate-900">
                              <SelectItem value="+91">🇮🇳 +91</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="relative flex-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                          <Input
                            id="phoneBody"
                            type="tel"
                            placeholder="Enter 10-digit number"
                            value={phoneBody}
                            onChange={(e) => setPhoneBody(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary font-mono text-base"
                            data-testid="phone-input"
                            maxLength={10}
                            autoFocus
                          />
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 font-medium">
                        Enter your WhatsApp number to receive an OTP verification token.
                      </p>
                      {phoneBody && phoneBody.length < 10 && (
                        <p className="text-[11px] text-red-500 font-semibold mt-1">
                          Please enter a valid 10-digit phone number.
                        </p>
                      )}
                      {phoneBody && phoneBody.length === 10 && (
                        <p className="text-[11px] text-green-600 font-semibold mt-1">
                          ✓ Valid 10-digit phone number.
                        </p>
                      )}
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 font-bold text-white hover:opacity-90 transition-all rounded-md"
                      style={{ backgroundColor: interactiveColor }}
                      disabled={requestOtp.isPending}
                      data-testid="request-otp-btn"
                    >
                      {requestOtp.isPending ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </form>
                )}

                {whatsappEnabled && step === 'otp' && (
                  <form onSubmit={handleVerifyOtp} className="space-y-6">
                    <div className="space-y-4">
                      <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
                        <div>
                          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Phone Number</span>
                          <p className="text-sm font-semibold font-mono text-slate-800">{phone}</p>
                        </div>
                        <Button
                          type="button"
                          variant="link"
                          onClick={() => {
                            setStep('phone');
                            setOtp('');
                          }}
                          className="p-0 h-auto text-xs font-bold hover:underline"
                          style={{ color: interactiveColor }}
                          data-testid="change-phone-btn"
                        >
                          Edit Phone
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-slate-600 tracking-wider uppercase block text-center mb-1">
                          Enter 6-Digit OTP
                        </Label>
                        <div className="flex justify-center">
                          <InputOTP 
                            maxLength={6} 
                            value={otp} 
                            onChange={setOtp}
                            data-testid="otp-input"
                          >
                            <InputOTPGroup>
                              <InputOTPSlot index={0} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={1} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={2} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={3} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={4} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={5} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                            </InputOTPGroup>
                          </InputOTP>
                        </div>
                        <p className="text-[11px] text-slate-500 text-center mt-2">
                          Check your WhatsApp for the 6-digit verification code.
                        </p>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 font-bold text-white hover:opacity-90 transition-all rounded-md"
                      style={{ backgroundColor: interactiveColor }}
                      disabled={verifyOtp.isPending || otp.length !== 6}
                      data-testid="verify-otp-btn"
                    >
                      {verifyOtp.isPending ? 'Verifying...' : 'Verify & Login'}
                    </Button>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResendOtp}
                      className="w-full h-11 font-semibold border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900 rounded-md"
                      disabled={resendCooldown > 0 || requestOtp.isPending}
                      data-testid="resend-otp-btn"
                    >
                      {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : 'Resend WhatsApp OTP'}
                    </Button>
                  </form>
                )}

                {whatsappEnabled && step === 'register_details' && (
                  <form onSubmit={handleRegisterDetailsSubmit} className="space-y-5">
                    <div className="space-y-4">
                      {/* Pre-filled, verified phone display */}
                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                          Verified Phone Number
                        </Label>
                        <div className="relative flex items-center">
                          <Phone className="absolute left-3 text-slate-400" size={20} />
                          <Input
                            type="text"
                            value={phone}
                            disabled
                            className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-500 font-mono text-base cursor-not-allowed"
                          />
                          <span className="absolute right-3 px-2 py-1 bg-green-50 text-green-700 border border-green-200 text-[10px] font-bold uppercase tracking-wider rounded">
                            Verified
                          </span>
                        </div>
                      </div>

                      {/* Full Name input */}
                      <div className="space-y-2">
                        <Label htmlFor="regFullName" className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                          Full Name
                        </Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                          <Input
                            id="regFullName"
                            type="text"
                            placeholder="Enter your full name"
                            value={regFullName}
                            onChange={(e) => setRegFullName(e.target.value)}
                            className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary text-base"
                            required
                            autoFocus
                          />
                        </div>
                        <p className="text-[11px] text-slate-500 font-medium">
                          Please enter your name to complete registration. No passwords or emails required.
                        </p>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 font-bold text-white hover:opacity-90 transition-all rounded-md mt-6"
                      style={{ backgroundColor: interactiveColor }}
                      disabled={registerCompleteMutation.isPending}
                    >
                      {registerCompleteMutation.isPending ? 'Registering...' : 'Complete Registration'}
                    </Button>
                  </form>
                )}

                {step !== 'register_details' && (
                  <>
                    {/* Divider — only meaningful when there's a WhatsApp OTP form above it */}
                    {whatsappEnabled && (
                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-slate-200" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-slate-400 font-bold">Or</span>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {process.env.REACT_APP_GOOGLE_CLIENT_ID && 
                       !process.env.REACT_APP_GOOGLE_CLIENT_ID.includes("your-google-client-id") &&
                       window.location.hostname !== "localhost" &&
                       window.location.hostname !== "127.0.0.1" ? (
                        <div id="googleLoginBtn" className="w-full overflow-hidden rounded-md min-h-[44px]"></div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleMockGoogleLogin}
                          className="w-full h-12 font-bold flex items-center justify-center gap-3 bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 shadow-sm rounded-md"
                          disabled={googleLogin.isPending}
                        >
                          <GoogleLogo size={20} weight="bold" style={{ color: '#4285F4' }} />
                          {googleLogin.isPending ? 'Connecting...' : 'Sign in with Google'}
                        </Button>
                      )}

                      <div className="text-center">
                        <Button
                          type="button"
                          variant="link"
                          onClick={() => setView('alternative')}
                          className="text-xs font-bold hover:underline p-0 h-auto bg-transparent border-0"
                          style={{ color: interactiveColor }}
                        >
                          Sign in with email & password instead
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              // Customer Portal: Alternative view is Email/Password
              <div className="space-y-6">
                <form onSubmit={handleLoginCredentials} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="emailOrPhone" className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                      Email or Phone
                    </Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                      <Input
                        id="emailOrPhone"
                        type="text"
                        placeholder="Enter email or phone"
                        value={emailOrPhone}
                        onChange={(e) => setEmailOrPhone(e.target.value)}
                        className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="password" className="text-xs font-bold text-slate-600 tracking-wider uppercase block">
                        Password
                      </Label>
                      <button
                        type="button"
                        onClick={() => {
                          setResetStep('phone');
                          setShowResetModal(true);
                        }}
                        className="text-xs font-semibold hover:underline"
                        style={{ color: interactiveColor }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 font-bold text-white hover:opacity-90 transition-all rounded-md mt-6"
                    style={{ backgroundColor: interactiveColor }}
                    disabled={loginCredentials.isPending}
                  >
                    {loginCredentials.isPending ? 'Logging in...' : 'Log In'}
                  </Button>
                </form>

                {whatsappEnabled && (
                  <div className="text-center mt-4 border-t border-slate-200 pt-4">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => { setView('default'); setStep('phone'); }}
                      className="text-xs font-bold hover:underline p-0 h-auto bg-transparent border-0"
                      style={{ color: interactiveColor }}
                    >
                      Log in with WhatsApp OTP instead
                    </Button>
                  </div>
                )}
              </div>
            )
          )}

          {/* Operations Portal View */}
          {isOps && (
            view === 'default' ? (
              // Ops Portal: Default view is Google SSO + Email/Password
              <div className="space-y-6">
                <div className="space-y-3">
                  {process.env.REACT_APP_GOOGLE_CLIENT_ID && 
                   !process.env.REACT_APP_GOOGLE_CLIENT_ID.includes("your-google-client-id") &&
                   window.location.hostname !== "localhost" &&
                   window.location.hostname !== "127.0.0.1" ? (
                    <div id="googleLoginBtn" className="w-full overflow-hidden rounded-md min-h-[44px]"></div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleMockGoogleLogin}
                      className="w-full h-12 font-bold flex items-center justify-center gap-3 bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 shadow-sm rounded-md"
                      disabled={googleLogin.isPending}
                    >
                      <GoogleLogo size={20} weight="bold" style={{ color: '#4285F4' }} />
                      {googleLogin.isPending ? 'Connecting...' : 'Sign in with Google'}
                    </Button>
                  )}
                  <p className="text-center text-xs text-slate-500 font-medium">
                    Google Workspace Single Sign-On (SSO)
                  </p>
                </div>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-400 font-bold">Or continue with</span>
                  </div>
                </div>

                <form onSubmit={handleLoginCredentials} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="emailOrPhone" className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                      Email or Phone
                    </Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                      <Input
                        id="emailOrPhone"
                        type="text"
                        placeholder="Enter email or phone number"
                        value={emailOrPhone}
                        onChange={(e) => setEmailOrPhone(e.target.value)}
                        className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-1">
                      <Label htmlFor="password" className="text-xs font-bold text-slate-600 tracking-wider uppercase block">
                        Password
                      </Label>
                      <button
                        type="button"
                        onClick={() => {
                          setResetStep('phone');
                          setShowResetModal(true);
                        }}
                        className="text-xs font-semibold hover:underline"
                        style={{ color: interactiveColor }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 font-bold text-white hover:opacity-90 transition-all rounded-md mt-6"
                    style={{ backgroundColor: interactiveColor }}
                    disabled={loginCredentials.isPending}
                  >
                    {loginCredentials.isPending ? 'Logging in...' : 'Log In'}
                  </Button>
                </form>

                {whatsappEnabled && (
                  <div className="text-center mt-4 border-t border-slate-200 pt-4">
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => { setView('alternative'); setStep('phone'); }}
                      className="text-xs font-bold hover:underline p-0 h-auto bg-transparent border-0"
                      style={{ color: interactiveColor }}
                    >
                      Log in with WhatsApp OTP instead
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              // Ops Portal: Alternative view is WhatsApp OTP
              <div className="space-y-6">
                {step === 'phone' ? (
                  <form onSubmit={handleRequestOtp} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="phoneBody" className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                        Phone Number
                      </Label>
                      <div className="flex gap-2">
                        <div className="w-[100px]">
                          <Select value={countryCode} onValueChange={setCountryCode}>
                            <SelectTrigger className="h-12 border-slate-300 bg-white text-slate-900 focus:border-primary">
                              <SelectValue placeholder="+91" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200 text-slate-900">
                              <SelectItem value="+91">🇮🇳 +91</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="relative flex-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                          <Input
                            id="phoneBody"
                            type="tel"
                            placeholder="Enter 10-digit number"
                            value={phoneBody}
                            onChange={(e) => setPhoneBody(e.target.value.replace(/\D/g, '').slice(0, 10))}
                            className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary font-mono text-base"
                            data-testid="phone-input"
                            maxLength={10}
                            autoFocus
                          />
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 font-bold text-white hover:opacity-90 transition-all rounded-md"
                      style={{ backgroundColor: interactiveColor }}
                      disabled={requestOtp.isPending}
                      data-testid="request-otp-btn"
                    >
                      {requestOtp.isPending ? 'Sending OTP...' : 'Send OTP'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyOtp} className="space-y-5">
                    <div className="space-y-4">
                      <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between">
                        <div>
                          <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Phone Number</span>
                          <p className="text-sm font-semibold font-mono text-slate-800">{phone}</p>
                        </div>
                        <Button
                          type="button"
                          variant="link"
                          onClick={() => {
                            setStep('phone');
                            setOtp('');
                          }}
                          className="p-0 h-auto text-xs font-bold hover:underline"
                          style={{ color: interactiveColor }}
                          data-testid="change-phone-btn"
                        >
                          Change number
                        </Button>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold text-slate-600 tracking-wider uppercase block text-center mb-1">
                          Enter OTP
                        </Label>
                        <div className="flex justify-center">
                          <InputOTP 
                            maxLength={6} 
                            value={otp} 
                            onChange={setOtp}
                            data-testid="otp-input"
                          >
                            <InputOTPGroup>
                              <InputOTPSlot index={0} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={1} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={2} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={3} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={4} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                              <InputOTPSlot index={5} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                            </InputOTPGroup>
                          </InputOTP>
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full h-12 font-bold text-white hover:opacity-90 transition-all rounded-md"
                      style={{ backgroundColor: interactiveColor }}
                      disabled={verifyOtp.isPending || otp.length !== 6}
                      data-testid="verify-otp-btn"
                    >
                      {verifyOtp.isPending ? 'Verifying...' : 'Verify & Login'}
                    </Button>
                  </form>
                )}

                <div className="text-center mt-4 border-t border-slate-200 pt-4">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setView('default')}
                    className="text-xs font-bold hover:underline p-0 h-auto bg-transparent border-0"
                    style={{ color: interactiveColor }}
                  >
                    Back to Google & Credentials login
                  </Button>
                </div>
              </div>
            )
          )}

          {/* Customer Self Registration link */}
          {!isOps && step !== 'register_details' && (
            <div className="text-center mt-6 border-t border-slate-200 pt-4">
              <span className="text-xs text-slate-500">Don't have an account? </span>
              <Link 
                to="/register" 
                className="text-xs font-bold hover:underline ml-1"
                style={{ color: interactiveColor }}
              >
                Sign Up
              </Link>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="sm:max-w-[425px] bg-white border border-slate-200 text-slate-900 shadow-2xl rounded-xl p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black text-slate-800 tracking-tight">
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Verify your identity via WhatsApp OTP
            </DialogDescription>
          </DialogHeader>

          {resetStep === 'phone' ? (
            <form onSubmit={handleRequestReset} className="space-y-5 mt-4">
              <div className="space-y-2">
                <Label htmlFor="resetPhoneBody" className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                  Phone Number (WhatsApp)
                </Label>
                <div className="flex gap-2">
                  <div className="w-[100px]">
                    <Select value={resetCountryCode} onValueChange={setResetCountryCode}>
                      <SelectTrigger className="h-12 border-slate-300 bg-white text-slate-900 focus:border-primary">
                        <SelectValue placeholder="+91" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 text-slate-900">
                        <SelectItem value="+91">🇮🇳 +91</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    <Input
                      id="resetPhoneBody"
                      type="tel"
                      placeholder="Enter 10-digit number"
                      value={resetPhoneBody}
                      onChange={(e) => setResetPhoneBody(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary font-mono text-base"
                      required
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowResetModal(false)}
                  className="flex-1 h-12 font-semibold border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 font-semibold text-white hover:opacity-90 transition-all rounded-md"
                  style={{ backgroundColor: interactiveColor }}
                  disabled={requestingReset}
                >
                  {requestingReset ? 'Sending...' : 'Send OTP'}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleConfirmReset} className="space-y-5 mt-4">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between text-xs text-slate-700">
                <div>
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Verification Phone</span>
                  <span className="text-sm font-semibold font-mono text-slate-800">{resetCountryCode} {resetPhoneBody}</span>
                </div>
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setResetStep('phone')}
                  className="p-0 h-auto text-xs hover:underline font-bold"
                  style={{ color: interactiveColor }}
                >
                  Edit Phone
                </Button>
              </div>

              <div className="space-y-3">
                <Label className="text-xs text-slate-600 uppercase tracking-wider font-bold block text-center mb-1">Enter 6-Digit OTP</Label>
                <div className="flex justify-center">
                  <InputOTP 
                    maxLength={6} 
                    value={resetOtp} 
                    onChange={setResetOtp}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                      <InputOTPSlot index={1} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                      <InputOTPSlot index={2} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                      <InputOTPSlot index={3} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                      <InputOTPSlot index={4} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                      <InputOTPSlot index={5} className="h-12 w-12 text-lg text-slate-900 border-slate-300 bg-white" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-xs font-bold text-slate-600 tracking-wider uppercase block mb-1.5">
                  New Password
                </Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                  <Input
                    id="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-primary"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setResetStep('phone');
                    setResetOtp('');
                    setNewPassword('');
                  }}
                  className="flex-1 h-12 font-semibold border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 font-semibold text-white hover:opacity-90 transition-all rounded-md"
                  style={{ backgroundColor: interactiveColor }}
                  disabled={confirmingReset || resetOtp.length !== 6 || newPassword.length < 6}
                >
                  {confirmingReset ? 'Resetting...' : 'Confirm Reset'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};